import { create } from 'zustand';
import type { House, Meter, Reading } from '../types/models.ts';
import { getHouses, getMeters, getReadings } from '../api/houses.ts';

interface HousesState {
  houses: House[];
  selectedHouseId: string | null;
  meters: Record<string, Meter[]>; // houseId → meters
  readings: Record<string, Reading[]>; // meterId → readings
  loading: boolean;
  error: string | null;

  loadHouses(): Promise<void>;
  selectHouse(houseId: string): void;
  loadMeters(houseId: string): Promise<void>;
  loadReadings(meterId: string): Promise<void>;
  addReadingToStore(meterId: string, reading: Reading): void;
  reset(): void;
}

export const useHousesStore = create<HousesState>((set, get) => ({
  houses: [],
  selectedHouseId: null,
  meters: {},
  readings: {},
  loading: false,
  error: null,

  async loadHouses() {
    set({ loading: true, error: null });
    try {
      const houses = await getHouses();
      set({ houses, loading: false });
      // Автовыбор первого дома
      if (houses.length > 0 && !get().selectedHouseId) {
        set({ selectedHouseId: houses[0].id });
      }
    } catch {
      set({ loading: false, error: 'Не удалось загрузить дома' });
    }
  },

  selectHouse(houseId) {
    set({ selectedHouseId: houseId });
  },

  async loadMeters(houseId) {
    try {
      const meters = await getMeters(houseId);
      set((s) => ({ meters: { ...s.meters, [houseId]: meters } }));
    } catch {
      // Не блокируем UI — просто пустой список
    }
  },

  async loadReadings(meterId) {
    try {
      const readings = await getReadings(meterId);
      set((s) => ({ readings: { ...s.readings, [meterId]: readings } }));
    } catch {
      // Не блокируем UI
    }
  },

  addReadingToStore(meterId, reading) {
    set((s) => ({
      readings: {
        ...s.readings,
        [meterId]: [reading, ...(s.readings[meterId] ?? [])],
      },
    }));
  },

  reset() {
    set({ houses: [], selectedHouseId: null, meters: {}, readings: {}, loading: false, error: null });
  },
}));
